import { useCallback, useEffect, useState } from "react";

import styled from "styled-components/macro";

import EditableExtendedField from "CrossFeatureComponents/ExtendedFields/EditableExtendedField";
import { useSignups } from "Signups/state";
import { motion } from "framer-motion";

function convertResponseData(responses, signupItem) {
  const { extendedFields } = signupItem;
  const resp = {};
  for (const fieldID in responses) {
    const response = responses[fieldID];
    const field = extendedFields.find(
      (f) => f.fieldID.toString() === fieldID.toString()
    );
    if (field) {
      switch (field.type) {
        case "picker": {
          if (response?.value?.values?.map) {
            resp[fieldID] = response.value.values.map((r) =>
              parseInt(r.optionListValueID)
            );
          }
          break;
        }
        case "address":
        case "date":
          resp[fieldID] = response.value;
          break;
        case "text":
        case "number": {
          resp[fieldID] = response.value.value;
          break;
        }
        default:
          break;
      }
    }
  }
  return resp;
}

const MappedEditableExtendedField = ({
  field,
  handleFieldChange,
  getFieldInputResponse,
  getFieldInputError,
  userID,
}) => {
  const fieldID = field.ID || field.fieldID;
  const handleOnChange = useCallback(
    (val) => handleFieldChange(fieldID, val),
    [handleFieldChange, fieldID]
  );
  return (
    <EditableExtendedField
      field={field}
      onChange={handleOnChange}
      value={getFieldInputResponse(fieldID, field.type)}
      error={getFieldInputError(fieldID)}
      accentColor="#278dc1"
      userID={userID}
    />
  );
};

export default function RespondentEntryExtendedFields({
  respondent,
  fields,
  signupItem,
}) {
  const [state, dispatch] = useSignups();
  let { userID, extendedFieldResponses } = respondent;
  if (!userID && respondent.ID) userID = respondent.ID;
  const validFieldResponses = convertResponseData(
    extendedFieldResponses,
    signupItem
  );
  const {
    extendedFieldInputResponses,
    extendedFieldInputErrors,
    currentSignupList,
  } = state;

  const [triggerScroll, setTriggerScroll] = useState(false);
  const [hasAlreadyScrolled, setHasAlreadyScrolled] = useState(false);

  useEffect(() => {
    if (triggerScroll && !hasAlreadyScrolled) {
      setTriggerScroll(false);
      const errorMessages = document.querySelectorAll(".FnrNoticeMessage");
      if (errorMessages && errorMessages?.length) {
        const top =
          window.pageYOffset + errorMessages[0].getBoundingClientRect().top;
        if (document.documentElement.scrollTo) {
          document.documentElement.scrollTo({
            top: top / 2,
            behavior: "smooth",
          });
        }
        setHasAlreadyScrolled(true);
      }
    }
  }, [triggerScroll, hasAlreadyScrolled]);

  const handleFieldChange = useCallback(
    (fieldID, value) => {
      dispatch({
        type: "updateExtendedFieldInputResponse",
        payload: {
          userID,
          fieldID,
          value,
          signupItemID: signupItem.ID,
        },
      });
    },
    [dispatch, userID, signupItem.ID]
  );

  const animations = {
    closed: { height: 0, opacity: 0 },
    open: { height: "auto", opacity: 1 },
  };

  const getDefaultResponse = (fieldID, type) => {
    switch (type) {
      case "picker":
        return [];
      case "address": {
        let field = fields.find((f) => f.fieldID === fieldID);
        return {
          country: field.defaultCountryID,
        };
      }
      default:
        return "";
    }
  };

  const getFieldInputResponse = (fieldID, type) => {
    const defaultResp = getDefaultResponse(fieldID, type);

    if (!extendedFieldInputResponses[userID]) {
      return defaultResp;
    } else if (extendedFieldInputResponses[userID][fieldID] === 0) {
      return extendedFieldInputResponses[userID][fieldID];
    } else {
      return extendedFieldInputResponses[userID][fieldID] || defaultResp;
    }
  };

  const getFieldInputError = (fieldID) => {
    if (!(userID in extendedFieldInputErrors)) return "";
    if (!extendedFieldInputErrors[userID].includes(fieldID)) return "";
    if (!triggerScroll) setTriggerScroll(true);
    return "Uh oh! Please fill out this field correctly.";
  };

  return (
    <Wrapper
      variants={animations}
      initial="closed"
      animate="open"
      exit="closed"
      key="fields_drawer"
      data-cy="RespondentEntryExtendedFields_Container"
    >
      <Inside>
        {fields.map((field) => (
          <MappedEditableExtendedField
            key={field.ID || field.fieldID}
            field={field}
            handleFieldChange={handleFieldChange}
            getFieldInputResponse={getFieldInputResponse}
            getFieldInputError={getFieldInputError}
            userID={userID}
          />
        ))}
        <BorderBottom />
      </Inside>
    </Wrapper>
  );
}

const Wrapper = styled(motion.div)`
  overflow: hidden;
`;
const Inside = styled.div`
  padding-top: 8px;
  padding-left: 34px;
  padding-right: 12px;
`;
const BorderBottom = styled.div`
  height: 1px;
  background: #eceff0;
  margin: 12px 0;
`;
